import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid
} from 'recharts';
import {
  Megaphone, ArrowUpDown, AlertTriangle, RefreshCw, ChevronDown
} from 'lucide-react';

const GROUP_BY_OPTIONS = [
  { value: 'utm_campaign', label: 'Campaign' },
  { value: 'utm_source', label: 'Source' },
  { value: 'utm_medium', label: 'Medium' },
];

const COLUMNS = [
  { key: 'group', label: 'Group', align: 'left', sortable: true },
  { key: 'leads_count', label: 'Leads', align: 'right', sortable: true },
  { key: 'quotes_count', label: 'Quotes', align: 'right', sortable: true },
  { key: 'bookings_count', label: 'Bookings', align: 'right', sortable: true },
  { key: 'lead_to_quote_rate', label: 'Lead\u2192Quote %', align: 'right', sortable: true },
  { key: 'quote_to_booking_rate', label: 'Quote\u2192Book %', align: 'right', sortable: true },
  { key: 'lead_to_booking_rate', label: 'Lead\u2192Book %', align: 'right', sortable: true },
];

const BAR_COLOR = '#3BA4C5';

export default function CampaignAttributionWidget({
  data, loading, error, groupBy, onGroupByChange, onRetry,
}) {
  const [sortKey, setSortKey] = useState('bookings_count');
  const [sortDir, setSortDir] = useState('desc');
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const rows = useMemo(() => {
    if (!data?.groups) return [];
    return data.groups.map(g => ({
      ...g,
      lead_to_booking_rate:
        g.leads_count > 0
          ? Math.round((g.bookings_count / g.leads_count) * 10000) / 100
          : 0,
    }));
  }, [data]);

  const sorted = useMemo(() => {
    if (!rows.length) return [];
    const copy = [...rows];
    copy.sort((a, b) => {
      const av = a[sortKey] ?? '';
      const bv = b[sortKey] ?? '';
      if (typeof av === 'number' && typeof bv === 'number') {
        return sortDir === 'asc' ? av - bv : bv - av;
      }
      const cmp = String(av).localeCompare(String(bv));
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [rows, sortKey, sortDir]);

  const chartData = useMemo(() => {
    if (!sorted.length) return [];
    const top10 = sorted.slice(0, 10).filter(r => r.bookings_count > 0);
    return top10.map(r => ({
      name: r.group.length > 18 ? r.group.slice(0, 16) + '\u2026' : r.group,
      bookings: r.bookings_count,
    }));
  }, [sorted]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const currentLabel = GROUP_BY_OPTIONS.find(o => o.value === groupBy)?.label || groupBy;

  return (
    <Card className="border border-slate-200/60 shadow-sm" data-testid="attribution-widget">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-sm font-semibold text-[#122033] font-[Manrope] flex items-center gap-2">
            <Megaphone className="h-4 w-4 text-[#3BA4C5]" />
            Campaign Attribution
          </CardTitle>
          <div className="relative">
            <Button
              variant="outline" size="sm"
              className="text-xs h-7 px-2.5 gap-1 text-slate-600"
              onClick={() => setDropdownOpen(o => !o)}
              data-testid="attribution-group-by-toggle"
            >
              {currentLabel}
              <ChevronDown className={`h-3 w-3 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
            </Button>
            {dropdownOpen && (
              <div className="absolute right-0 top-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg z-20 py-1 min-w-[130px]"
                data-testid="attribution-group-by-dropdown"
              >
                {GROUP_BY_OPTIONS.map(o => (
                  <button
                    key={o.value}
                    className={`w-full text-left text-xs px-3 py-1.5 hover:bg-slate-50 transition-colors ${
                      o.value === groupBy ? 'text-[#3BA4C5] font-semibold bg-slate-50' : 'text-slate-600'
                    }`}
                    onClick={() => { onGroupByChange(o.value); setDropdownOpen(false); }}
                    data-testid={`group-by-${o.value}`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Error state */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 flex items-center justify-between mb-4"
            data-testid="attribution-error"
          >
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-500" />
              <span className="text-sm text-red-700">{error}</span>
            </div>
            <Button size="sm" variant="outline" onClick={onRetry} data-testid="attribution-retry">
              <RefreshCw className="h-3 w-3 mr-1" /> Retry
            </Button>
          </div>
        )}

        {/* Loading skeleton */}
        {loading && !error && (
          <div className="space-y-3" data-testid="attribution-loading">
            <div className="h-40 bg-slate-50 animate-pulse rounded" />
            <div className="space-y-2">
              {[1,2,3,4,5].map(i => <div key={i} className="h-8 bg-slate-50 animate-pulse rounded" />)}
            </div>
          </div>
        )}

        {/* Empty state */}
        {!loading && !error && rows.length === 0 && (
          <div className="py-10 text-center" data-testid="attribution-empty">
            <Megaphone className="h-8 w-8 text-slate-200 mx-auto mb-2" />
            <p className="text-sm text-slate-400">No attribution data in selected window</p>
          </div>
        )}

        {/* Content */}
        {!loading && !error && rows.length > 0 && (
          <>
            {/* Bar chart — top 10 by bookings */}
            {chartData.length > 0 && (
              <div className="mb-5" data-testid="attribution-chart">
                <p className="text-[10px] uppercase tracking-wider text-slate-400 mb-2 font-medium">
                  Top {chartData.length} by Bookings
                </p>
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={chartData} barSize={28} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#64748B' }} axisLine={false} tickLine={false} interval={0} angle={-20} textAnchor="end" height={40} />
                    <YAxis tick={{ fontSize: 10, fill: '#94A3B8' }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <Tooltip
                      formatter={(v) => [v, 'Bookings']}
                      contentStyle={{ fontSize: 12, borderRadius: 8 }}
                    />
                    <Bar dataKey="bookings" fill={BAR_COLOR} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Table */}
            <div className="overflow-x-auto" data-testid="attribution-table-wrapper">
              <table className="w-full text-sm" data-testid="attribution-table">
                <thead>
                  <tr className="border-b border-slate-100">
                    {COLUMNS.map(col => (
                      <th
                        key={col.key}
                        className={`py-2 text-[10px] uppercase text-slate-400 font-medium ${
                          col.align === 'right' ? 'text-right' : 'text-left'
                        } ${col.sortable ? 'cursor-pointer select-none hover:text-slate-600' : ''}`}
                        onClick={() => col.sortable && handleSort(col.key)}
                        data-testid={`attr-col-${col.key}`}
                      >
                        <span className="inline-flex items-center gap-0.5">
                          {col.label}
                          {col.sortable && sortKey === col.key && (
                            <ArrowUpDown className="h-2.5 w-2.5 text-[#3BA4C5]" />
                          )}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((row, i) => (
                    <tr
                      key={row.group}
                      className={`border-b border-slate-50 hover:bg-slate-50/50 transition-colors ${
                        row.group === 'UNKNOWN' ? 'bg-amber-50/30' : ''
                      }`}
                      data-testid={`attr-row-${i}`}
                    >
                      <td className="py-2 text-xs font-medium text-[#122033] font-mono max-w-[200px] truncate" title={row.group}>
                        {row.group}
                      </td>
                      <td className="py-2 text-right text-xs text-slate-600">{row.leads_count}</td>
                      <td className="py-2 text-right text-xs text-slate-600">{row.quotes_count}</td>
                      <td className="py-2 text-right text-xs font-semibold text-[#122033]">{row.bookings_count}</td>
                      <td className="py-2 text-right text-xs text-slate-500">{row.lead_to_quote_rate}%</td>
                      <td className="py-2 text-right text-xs text-slate-500">{row.quote_to_booking_rate}%</td>
                      <td className="py-2 text-right text-xs text-emerald-600 font-medium">{row.lead_to_booking_rate}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {data?.count != null && (
                <p className="text-[10px] text-slate-300 mt-2 text-right" data-testid="attribution-count">
                  Showing {sorted.length} of {data.count} groups
                </p>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
